import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { ensureArray } from '@/lib/utils/ensure-array';
import type {
  BulkLocationAddRequest,
  BulkLocationRemoveRequest,
  LocationCreateRequest,
  LocationRow,
  LocationUpdateRequest,
  UserInLocationRow,
} from './types';

export const locationKeys = {
  all: ['locations'] as const,
  list: () => ['locations', 'list'] as const,
  detail: (locationId: string | number) => ['locations', 'detail', locationId] as const,
  members: (locationId: string | number) =>
    ['locations', 'members', locationId] as const,
};

export function useLocationsList() {
  return useQuery({
    queryKey: locationKeys.list(),
    queryFn: () => api.get<LocationRow[]>('/api/obd/locations'),
    staleTime: 30_000,
    select: (data) => ensureArray<LocationRow>(data),
  });
}

/**
 * Lightweight detail used by the update form — the legacy didn't expose
 * a dedicated endpoint so we cherry-pick the matching row from the list
 * cache.
 */
export function useLocation(locationId: string | number | null) {
  const list = useLocationsList();
  const row =
    locationId === null
      ? undefined
      : list.data?.find((l) => String(l.locationId) === String(locationId));
  return { ...list, data: row };
}

function useInvalidateLocations() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: locationKeys.all });
}

export function useCreateLocation() {
  const invalidate = useInvalidateLocations();
  return useMutation({
    mutationFn: (req: LocationCreateRequest) =>
      api.post<{ message?: string }>('/api/obd/location', req),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateLocation() {
  const invalidate = useInvalidateLocations();
  return useMutation({
    mutationFn: (req: LocationUpdateRequest) =>
      api.post<{ message?: string }>('/api/obd/location/update', req),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteLocation() {
  const invalidate = useInvalidateLocations();
  return useMutation({
    mutationFn: (locationId: number) =>
      api.delete<{ message?: string }>(`/api/obd/location/${locationId}`),
    onSuccess: () => invalidate(),
  });
}

/* ---------------- Bulk operations ---------------- */

/** POST /api/obd/user/location/list — users currently attached to a location. */
export function useUsersInLocation(locationId: string | number | null) {
  return useQuery({
    queryKey: locationKeys.members(locationId ?? 'none'),
    queryFn: () =>
      api.post<UserInLocationRow[]>('/api/obd/user/location/list', {
        locationId: String(locationId),
      }),
    enabled: Boolean(locationId),
    staleTime: 30_000,
    select: (data) => ensureArray<UserInLocationRow>(data),
  });
}

export function useBulkAddLocationUsers() {
  const invalidate = useInvalidateLocations();
  return useMutation({
    mutationFn: (req: BulkLocationAddRequest) =>
      api.post<{ message?: string }>('/api/obd/bulk/user/location/addition', req),
    onSuccess: () => invalidate(),
  });
}

export function useBulkRemoveLocationUsers() {
  const invalidate = useInvalidateLocations();
  return useMutation({
    mutationFn: (req: BulkLocationRemoveRequest) =>
      api.post<{ message?: string }>('/api/obd/bulk/user/location/deletion', {
        ...req,
        swapLocationId: req.swapLocationId ?? '',
      }),
    onSuccess: () => invalidate(),
  });
}
